import { useTranslation } from 'react-i18next';
import { ExternalLink, Minus, ScrollText, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { useSystemDocumentsStore } from '@/stores/systemDocuments';
import { useUIStore } from '@/stores/ui';
import { LEGAL_DOCUMENT_ROUTES } from '@/components/legal/SystemDocumentAcceptanceModal';
import type { SystemDocumentKey } from '@/services/systemDocumentService';

interface LegalDocumentItem {
  key: SystemDocumentKey;
  labelKey: string;
  fallbackLabel: string;
  icon: React.ReactNode;
}

const LEGAL_DOCUMENT_ITEMS: LegalDocumentItem[] = [
  {
    key: 'terms_of_use',
    labelKey: 'system_documents.terms_of_use',
    fallbackLabel: 'Termos de Uso',
    icon: <ScrollText className="h-4 w-4" />,
  },
  {
    key: 'privacy_policy',
    labelKey: 'system_documents.privacy_policy',
    fallbackLabel: 'Política de Privacidade',
    icon: <ShieldCheck className="h-4 w-4" />,
  },
];

export function LegalDocumentsDrawer() {
  const { t } = useTranslation();
  const isOpen = useSystemDocumentsStore((s) => s.isLegalDocumentsDrawerOpen);
  const close = useSystemDocumentsStore((s) => s.closeLegalDocumentsDrawer);
  const sidebarCollapsed = useUIStore((state) => state.sidebarCollapsed);

  if (!isOpen) return null;

  const panelLeft = sidebarCollapsed ? 82 : 268;

  const handleOpenDocument = (key: SystemDocumentKey) => {
    window.open(LEGAL_DOCUMENT_ROUTES[key], '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-[120]">
      <motion.button
        type="button"
        aria-label={t('common.close') || 'Fechar'}
        onClick={close}
        className="absolute inset-0 bg-black/20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      />

      <div
        className="absolute right-0 flex justify-start"
        style={{
          left: panelLeft,
          bottom: 12,
          paddingInline: 10,
        }}
      >
        <motion.div
          className="rounded-[28px] border border-[#9cc8ff] bg-background shadow-[0_10px_28px_rgba(59,130,246,0.14)] flex flex-col w-full"
          style={{
            width: `min(400px, calc(100vw - ${panelLeft + 24}px))`,
            maxHeight: 'calc(100vh - 96px)',
          }}
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.24, ease: 'easeOut' }}
        >
          <div className="border-b bg-transparent px-5 py-4 relative">
            <button
              type="button"
              aria-label={t('common.close') || 'Fechar'}
              onClick={close}
              className="absolute right-5 top-1/2 -translate-y-1/2 p-1 text-[#6b7280] hover:text-[#374151]"
            >
              <Minus className="h-5 w-5 hover:text-destructive" />
            </button>

            <p className="pr-10 text-[1.2rem] font-semibold text-[#111827] leading-none">
              {t('system_documents.drawer_title') || 'Termos e Autorizações'}
            </p>
          </div>

          <div className="px-5 py-4">
            <div className="space-y-0.5">
              {LEGAL_DOCUMENT_ITEMS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => handleOpenDocument(item.key)}
                  className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition hover:bg-accent/30"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {item.icon}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[0.88rem] font-semibold text-card-foreground">
                    {t(item.labelKey) || item.fallbackLabel}
                  </span>
                  <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
